import * as d3 from 'd3';
import './d3-deficitchart.css';
import { colors, closest, DURATION } from '../utils';
import draghandle from './d3-draghandle';

export default function () {
  const margin = { top: 10, right: 120, bottom: 70, left: 60 };

  let _isResize;
  let width = 400;
  let height = 300;
  let svg;
  let t;
  let _data;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let color;
  let handle;
  let date;
  let asset;

  const dispatch = d3.dispatch('selected');

  function chart(selection, isResize) {
    selection.each(function (data) {
      const duration = isResize ? 0 : DURATION;

      _data = data;
      _isResize = isResize;
      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      t = d3.transition().duration(duration);

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();

        g.append('g')
          .attr('class', 'deficit-chart__projections');

        g.append('g')
          .attr('class', 'deficit-chart__guideline');

        g.append('g')
          .attr('class', 'deficit-chart__bar');

        createGuideline();
        createHandle();
        createBar();
        createLabel();
      }

      svg.attr('width', width)
        .attr('height', height);

      drawAxes();
      drawHandle();
      drawProjections();
      drawGuideline();
      drawBar();
      drawLabel();
    });
  }

  function createScales() {
    const paddingFraction = 0.1;
    x = d3.scaleTime().rangeRound([0, chartWidth]);
    y = d3.scaleLinear().rangeRound([chartHeight, 0]);
    color = d3.scaleOrdinal(d3.schemeCategory10);

    const minY = d3.min([0, d3.min(_data, function (d) { return d3.min(d.values, function (c) { return c.deficit; }); })]);
    const maxY = d3.max(_data, function (d) { return d3.max(d.values, function (c) { return c.deficit; }); });
    const widthY = maxY - minY;

    x.domain([
      d3.min(_data, function (d) { return d3.min(d.values, function (c) { return c.date; }); }),
      d3.max(_data, function (d) { return d3.max(d.values, function (c) { return c.date; }); }),
    ]);

    y.domain([minY - paddingFraction * widthY, maxY + paddingFraction * widthY]).nice();
    color.domain(_data.map(function (d) { return d.asset; }));
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'deficit-chart__axis deficit-chart__axis--y');

    const xAxis = svg.select('g')
      .append('g')
      .attr('class', 'deficit-chart__axis deficit-chart__axis--x');

    xAxis.append('line')
      .attr('class', 'baseline-halo');

    xAxis.append('line')
      .attr('class', 'baseline');
  }

  function createLabel() {
    svg.append('text')
      .attr('class', 'deficit-chart__label deficit-chart__label--y')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Deficit (£M)');

    svg.append('text')
      .attr('class', 'deficit-chart__label deficit-chart__label--x')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Year');
  }

  function createHandle() {
    const values = _data.find(function (d) { return d.asset === asset; }).values;
    const dates = values.map(function (d) { return d.date; });

    handle = draghandle().height(chartHeight)
      .extent([x(dates[0]), x(dates[dates.length - 1])])
      .on('dragended', function dragended(d) {
        const date = x.invert(d);
        const dates = _data[0].values.map(function (d) { return d.date; });

        const { curr } = closest(date, dates);

        dispatch.call('selected', chart, 'date', d3.timeFormat('%d-%b-%Y')(curr));
      });

    const handleContainer = svg.select('g');
    handleContainer.datum(x(d3.timeParse('%d-%b-%Y')(date))).call(handle);
  }

  function createBar() {
    const bar = svg.select('.deficit-chart__bar');

    bar.append('rect');

    bar.append('text')
      .attr('text-anchor', 'middle')
      .attr('font-weight', 'bold');
  }

  function createGuideline() {
    svg.select('.deficit-chart__guideline')
      .append('line')
      .attr('x1', 0)
      .attr('stroke', 'black')
      .attr('stroke-width', 1);
  }

  function drawAxes() {
    const xAxis = svg.select('.deficit-chart__axis--x');

    xAxis.attr('transform', 'translate(0,' + chartHeight + ')')
      .call(d3.axisBottom(x).tickFormat(d3.timeFormat('%Y')).tickSize(-chartHeight))
      .select('.domain').remove();

    const baselineYOffset = 4;

    xAxis.select('.baseline-halo')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', baselineYOffset)
      .attr('y2', baselineYOffset)
      .attr('stroke', '#bbb')
      .attr('stroke-width', 6)
      .attr('stroke-linecap', 'round');

    xAxis.select('.baseline')
      .attr('x1', 0)
      .attr('x2', chartWidth)
      .attr('y1', baselineYOffset)
      .attr('y2', baselineYOffset)
      .attr('stroke', '#eee')
      .attr('stroke-width', 4)
      .attr('stroke-linecap', 'round');

    xAxis.selectAll('text')
      .attr('dx', '-14px')
      .attr('dy', '14px')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    xAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    const yAxis = svg.select('.deficit-chart__axis--y')
      .call(d3.axisLeft(y).tickFormat(function (d) { return d === 0 ? "0" : d3.format(',.2r')(d / 1e6); }).tickSize(-chartWidth));

    yAxis.select('.domain').remove();

    yAxis.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    yAxis.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);
  }

  function drawProjections() {
    const projection = svg.select('g.deficit-chart__projections')
      .selectAll('.deficit-chart__projection')
      .data(_data);

    const basisLine = d3.line()
      .curve(d3.curveCardinal)
      .x(function (d) { return x(d.date); })
      .y(function (d) { return y(d.deficit); });

    const projectionEnter = projection.enter()
      .append('g')
      .attr('class', 'deficit-chart__projection')
      .attr('cursor', 'pointer')
      .on('click', function (d) {
        dispatch.call('selected', chart, 'asset', d.asset);
      });

    projectionEnter.append('path')
      .attr('class', function (d) { return 'deficit-chart__projection--visible deficit-chart__projection--' + d.asset; })
      .attr('fill', 'none')
      .merge(projection.select('.deficit-chart__projection--visible'))
      .transition(t)
      .attr('stroke-width', function (d) { return d.asset === asset ? 4 : 2; })
      .attr('stroke', function (d) { return d.asset === asset ? colors.red : colors.lightgray; })
      .attr('d', function (d) { return basisLine(d.values); });

    projectionEnter.append('path')
      .attr('class', function (d) { return 'deficit-chart__projection--invisible deficit-chart__projection--' + d.asset; })
      .attr('fill', 'none')
      .attr('stroke-width', 6)
      .attr('stroke', 'transparent')
      .merge(projection.select('.deficit-chart__projection--visible'))
      .transition(t)
      .attr('d', function (d) { return basisLine(d.values); });

    projectionEnter.append('text')
      .attr('class', 'deficit-chart__projection--label')
      .attr('y', function (d) { return y(d.values[d.values.length - 1].deficit) })
      .attr('x', chartWidth)
      .attr('dy', '0.3em')
      .attr('dx', '0.9em')
      .merge(projection.select('text'))
      .transition(t)
      .attr('x', chartWidth)
      .attr('y', function (d) { return y(d.values[d.values.length - 1].deficit) })
      .attr('font-weight', function (d) { return d.asset === asset ? 'bold' : 'normal'; })
      .text(function (d) { return d.asset });
  }

  function drawHandle() {
    const values = _data.find(function (x) { return x.asset === asset; }).values;
    const dates = values.map(function (d) { return d.date; });

    const handleContainer = svg.select('g');
    handleContainer.datum(x(d3.timeParse('%d-%b-%Y')(date))).call(handle.animate(!_isResize).height(chartHeight).extent([x(dates[0]), x(dates[dates.length - 1])]));
  }

  function drawLabel() {
    svg.select('.deficit-chart__label--y')
      .attr('transform', 'translate(10, ' + chartHeight / 1.6 + ')rotate(-90)');

    svg.select('.deficit-chart__label--x')
      .attr('x', width / 2)
      .attr('y', chartHeight + margin.top + margin.bottom - 20);
  }

  function drawGuideline() {
    svg.select('.deficit-chart__guideline line')
      .attr('x2', chartWidth + margin.right)
      .attr('y1', y(0))
      .attr('y2', y(0));
  }

  function drawBar() {
    const deficit = _data.find(function (x) { return x.asset === asset; })
      .values
      .find(function (x) { return d3.timeFormat('%d-%b-%Y')(x.date) === date; }).deficit;

    const barWidth = 30;
    const gap = 75;

    svg.select('.deficit-chart__bar rect')
      .transition(t)
      .attr('x', chartWidth + gap)
      .attr('y', deficit > 0 ? y(deficit) : y(0))
      .attr('width', barWidth)
      .attr('height', Math.abs(y(0) - y(deficit)))
      .attr('fill', deficit > 0 ? colors.con : colors.pro);

    svg.select('.deficit-chart__bar text')
      .transition(t)
      .attr('x', chartWidth + gap + barWidth / 2)
      .attr('y', y(deficit))
      .attr('dy', deficit > 0 ? '-4px' : '14px')
      .text(d3.formatPrefix(',.0', 1e6)(deficit));
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.date = function (value) {
    if (!arguments.length) return date;
    date = value;
    return chart;
  }

  chart.asset = function (value) {
    if (!arguments.length) return asset;
    asset = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-deficitchart.js